import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

export type DropdownOption<T extends string = string> = {
  value: T;
  label?: ReactNode;
};

type Props<T extends string> = {
  value: T | null | undefined;
  options: ReadonlyArray<DropdownOption<T> | T>;
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  minWidth?: number | string;
};

function normalize<T extends string>(
  options: ReadonlyArray<DropdownOption<T> | T>,
): ReadonlyArray<DropdownOption<T>> {
  return options.map((o) => (typeof o === "string" ? { value: o } : o));
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  placeholder = "Select",
  className = "",
  minWidth = 220,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const opts = normalize(options);
  const current = opts.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative ${className}`} style={{ minWidth }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "w-full px-3 py-2.5 text-left bg-mef-subtle rounded-xs",
          "border-[1.2px] border-mef-gray hover:border-mef-gray-tint",
          "flex items-center justify-between gap-2 cursor-pointer",
          "mef-focus-ring",
          current ? "text-black/80" : "text-black/40",
        ].join(" ")}
      >
        <span className="truncate">{current?.label ?? current?.value ?? placeholder}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="text-black/40 shrink-0">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[1100] bg-white border-[1.2px] border-mef-gray rounded-xs shadow-mef-popup py-1 max-h-72 overflow-auto"
        >
          {opts.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={[
                  "w-full px-3 py-2 text-left text-[13px] leading-4 font-medium",
                  active
                    ? "bg-mef-green/20 text-mef-green-text"
                    : "text-black/80 hover:bg-mef-surface-2",
                ].join(" ")}
              >
                {o.label ?? o.value}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
